import { DIGISAKA_API_ORIGIN } from '../../../config/digisakaApiOrigin';
import { fetchTitilerPointValue } from '../utils/titilerPoint';

/**
 * Nationwide irrigation type, second half of September 2026.
 * Band 1 of the 7-band stack is irrigation status. The other bands are
 * event counts, rainfall, growth stage, and quality, and are not drawn.
 * Integer classes: -2 no observation | 0 no irrigation | 1 irrigated | 2 rainfed | 3 no active crop.
 * Nodata -1 is not cropland. Only 0, 1, and 2 are painted.
 */
export const IRRIGATION_TYPE_COG_URL =
  'https://digisaka.s3.amazonaws.com/irrigation2026_PHILIPPINES_202609H2.tiff';

/** Native footprint, EPSG:32651 (UTM zone 51N). */
export const IRRIGATION_TYPE_NATIVE_BBOX: [number, number, number, number] = [
  -456200, 508000, 898200, 2336100,
];

/** Same footprint in EPSG:4326, for fitting the map view. */
export const IRRIGATION_TYPE_BBOX: [number, number, number, number] = [
  113.836, 4.544, 126.832, 21.083,
];

export const IRRIGATION_TYPE_LABEL = 'Sep 2026 · H2';

export const IRRIGATION_TYPE_CLASSES = [
  { value: -2, name: 'No observation', color: '#bdbdbd', drawn: false, pixels: 223, hectares: 223, share: 0 },
  { value: 0, name: 'No irrigation', color: '#d9d9d9', drawn: true, pixels: 28020, hectares: 28020, share: 0.9 },
  { value: 1, name: 'Irrigated', color: '#114c7f', drawn: true, pixels: 5622, hectares: 5622, share: 0.2 },
  { value: 2, name: 'Rainfed', color: '#4adbd9', drawn: true, pixels: 626938, hectares: 626938, share: 20.9 },
  { value: 3, name: 'No active crop', color: '#eeeeee', drawn: false, pixels: 2342242, hectares: 2342242, share: 78 },
] as const;

export const IRRIGATION_TYPE_LEGEND = {
  colors: IRRIGATION_TYPE_CLASSES.filter((row) => row.drawn).map((row) => row.color),
  labels: IRRIGATION_TYPE_CLASSES.filter((row) => row.drawn).map((row) => row.name),
  description:
    'Philippines irrigation type for the second half of September 2026. No irrigation, irrigated, and rainfed are drawn. No active crop and no observation stay transparent.',
};

const TITILER_TILE_BASE = DIGISAKA_API_ORIGIN + '/api/tiles/WebMercatorQuad/{z}/{x}/{y}@2x.png';

const IRRIGATION_TYPE_COLORMAP: Record<string, string> = Object.fromEntries(
  IRRIGATION_TYPE_CLASSES.filter((row) => row.drawn).map((row) => [String(row.value), row.color])
);

export async function fetchIrrigationTypePoint(
  lon: number,
  lat: number,
  signal?: AbortSignal
): Promise<{ value: number | null; label: string; color: string }> {
  const raw = await fetchTitilerPointValue(IRRIGATION_TYPE_COG_URL, lon, lat, {
    bidx: 1,
    nodata: '-1',
    signal,
  });
  const value = raw == null ? null : Math.round(raw);
  const row = IRRIGATION_TYPE_CLASSES.find((item) => item.value === value);
  if (!row) {
    return { value: null, label: 'No data', color: '#9ca3af' };
  }
  return { value: row.value, label: row.name, color: row.color };
}

export function buildIrrigationTypeTileUrl(cogUrl: string = IRRIGATION_TYPE_COG_URL): string {
  const params = new URLSearchParams();
  params.set('url', cogUrl);
  params.set('bidx', '1');
  params.set('colormap', JSON.stringify(IRRIGATION_TYPE_COLORMAP));
  params.set('nodata', '-1');
  params.set('resampling', 'nearest');
  return `${TITILER_TILE_BASE}?${params.toString()}`;
}
